import { FC, useMemo } from 'react';
import { Flex } from 'antd';
import {
  ActionButton,
  DividerHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
} from '../../../common/components';
import { getMapBy } from '../../../common/utils';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { AlgorithmTargetRuleType } from '../../types';
import { useGetAlgorithmMappingsQuery } from '../mappings/query/useGetAlgorithmMappingsQuery';
import { AlgorithmType } from '../schema/zAlgorithmType';
import { useGetAlgorithmSourcesQuery } from '../sources/query/useGetAlgorithmSourcesQuery';
import { AlgorithmTarget } from './AlgorithmTarget';
import { AlgorithmTargetRules } from './AlgoritmTargetRules';
import { AlgorithmTargetMappingsGraph } from './graph/AlgorithmTargetMappingsGraph';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargetGraphicViewModal.translations.json';
import { AlgorithmTargetFormDataType } from './schema/types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-targets-graphic-read-only-modal',
);
addTranslations(translations);

export interface AlgorithmTargetGraphicViewModalProps {
  algorithmId: number;
  algorithmTypeCode: AlgorithmType;
  target: AlgorithmTargetFormDataType;
  onClose?: () => void;
}

export const AlgorithmTargetGraphicViewModal: FC<
  AlgorithmTargetGraphicViewModalProps
> = (props) => {
  const { algorithmId, algorithmTypeCode, target, onClose } = props;
  const { t } = useTranslation();

  const { data: algorithmSources, isLoading: isLoadingSources } =
    useGetAlgorithmSourcesQuery({
      id: algorithmId,
    });

  const { data: algorithmMappings, isLoading: isLoadingMappings } =
    useGetAlgorithmMappingsQuery({
      id: algorithmId,
    });

  const mappings = algorithmMappings?.mappings;
  const sources = algorithmSources?.sources;
  const isLoading = isLoadingSources || isLoadingMappings;

  const mapRules = useMemo(
    () =>
      target?.rules.filter((r) => r.ruleType === AlgorithmTargetRuleType.MAP) ||
      [],
    [target],
  );

  const ruleRules = useMemo(
    () =>
      target?.rules.filter(
        (r) => r.ruleType === AlgorithmTargetRuleType.RULE,
      ) || [],
    [target],
  );

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const requiredKeysByKeys = useMemo(
    () => getMapBy(dataObjectDescriptionVersion.requiredKeys.reverse(), 'key'),
    [dataObjectDescriptionVersion],
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="70rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`) + ' ' + target.sequenceNumber}</ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <AlgorithmTarget
          target={target}
          mappings={algorithmMappings?.mappings || []}
          requiredKeysByKeys={requiredKeysByKeys}
        >
          <Padding top>
            <DividerHeader
              id="algorithm-target-map"
              title={t('mapTypeRules.label')}
              count={mapRules.length}
            >
              <AlgorithmTargetMappingsGraph
                mappings={mappings || EMPTY_ARRAY}
                sources={sources || EMPTY_ARRAY}
                isLoading={isLoading}
                algorithmTypeCode={algorithmTypeCode}
                readOnly
                rules={target?.rules}
              />
            </DividerHeader>
            <DividerHeader
              id="algorithm-target-map"
              title={t('ruleTypeRules.label')}
              count={ruleRules.length}
            >
              <AlgorithmTargetRules
                mappings={mappings || EMPTY_ARRAY}
                sources={sources || EMPTY_ARRAY}
                isLoading={isLoading}
                rules={ruleRules}
              />
            </DividerHeader>
          </Padding>
        </AlgorithmTarget>
      </Padding>
    </Modal>
  );
};

const EMPTY_ARRAY: any[] = [];
